/**
 * CORE-928 — публичная страница программы до покупки: правила отдельно от
 * вёрстки (§5 ТЗ, Макет 9).
 *
 * Страница показывает только то, что мастер заполнил в flow создания, и ни
 * одного закрытого материала. Здесь живёт то, что нельзя проверить глазами:
 * как состав сворачивается в счётчики, как звучит срок доступа и что стоит на
 * месте «Купить программу» у того, кому покупать не нужно или нельзя.
 */

import type {
  PublicProgramItem,
  PublicProgramOutline,
  PublicProgramPage,
} from '../../types/programs';
import { purchaseBlock, purchaseBlockMessage } from './programPurchase';
import { PROGRAM_LANGUAGES } from '../../components/dashboard/programs/programSteps';

// ─────────────────────────────────────────────────────────────────────────────
// Язык
// ─────────────────────────────────────────────────────────────────────────────

/**
 * «Русский» вместо «ru». Справочник тот же, из которого мастер выбирал на
 * шаге 1; незнакомый код печатается как есть, а не проглатывается.
 */
export function programLanguageLabel(code: string | null): string | null {
  if (!code) return null;
  return PROGRAM_LANGUAGES.find((l) => l.code === code)?.label ?? code;
}

// ─────────────────────────────────────────────────────────────────────────────
// Состав
// ─────────────────────────────────────────────────────────────────────────────

/** Четыре типа материала так, как их различает страница; порядок — как в макете. */
export type SummaryKind = 'practice' | 'media' | 'live' | 'file';

const SUMMARY_ORDER: SummaryKind[] = ['practice', 'media', 'live', 'file'];

/**
 * К какой группе относится материал. Бэк отдаёт подвиды практик
 * (`meditation`, `breathing`) отдельными строками — для человека это всё
 * «практики», и считать их врозь значило бы показывать «1 практика · 1
 * медитация · 1 дыхание» там, где макет обещает «3 практики».
 */
function summaryKind(kind: string): SummaryKind | null {
  switch (kind) {
    case 'practice':
    case 'meditation':
    case 'breathing':
      return 'practice';
    case 'media':
    case 'live':
    case 'file':
      return kind;
    default:
      return null;
  }
}

/** Русское множественное: forms = [1, 2–4, 5+]. */
function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

const KIND_FORMS: Record<SummaryKind, [string, string, string]> = {
  practice: ['практика', 'практики', 'практик'],
  media: ['медиа', 'медиа', 'медиа'],
  live: ['Live-сессия', 'Live-сессии', 'Live-сессий'],
  file: ['файл', 'файла', 'файлов'],
};

export interface SectionSummaryEntry {
  kind: SummaryKind;
  /** «2 практики», «1 медиа», «1 Live-сессия», «1 файл». */
  label: string;
}

/**
 * Состав раздела счётчиками по типам — то, что видно до покупки. Названий
 * материалов здесь нет намеренно: страница показывает, из чего программа
 * состоит, а не что именно в ней лежит.
 */
export function sectionSummary(items: PublicProgramItem[]): SectionSummaryEntry[] {
  const counts = new Map<SummaryKind, number>();
  for (const it of items) {
    const kind = summaryKind(it.kind);
    if (!kind) continue;
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  return SUMMARY_ORDER
    .filter((kind) => (counts.get(kind) ?? 0) > 0)
    .map((kind) => {
      const n = counts.get(kind) ?? 0;
      return { kind, label: `${n} ${plural(n, KIND_FORMS[kind])}` };
    });
}

/**
 * CORE-1564 — состав всей программы одной строкой: «6 практик · 2 Live-сессии ·
 * 1 файл». Юрист 29.10: программа одной ценой продаётся, если до оплаты видно,
 * что в неё входит, сколько стоит и на какой срок. Экран покупки — последний
 * шаг перед оплатой, и состав на нём стоит рядом с ценой и сроком. `null` —
 * считать нечего (мастер ещё собирает состав).
 */
export function programContentsLine(outline: PublicProgramOutline | null | undefined): string | null {
  const items = (outline?.sections ?? []).flatMap((s) => s.items);
  const entries = sectionSummary(items);
  return entries.length === 0 ? null : entries.map((e) => e.label).join(' · ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Срок доступа
// ─────────────────────────────────────────────────────────────────────────────

/** Срок в человеческих единицах: «8 недель», «6 месяцев», «45 дней». */
export function formatAccessSpan(days: number): string {
  if (days % 30 === 0) {
    const months = days / 30;
    return `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])}`;
  }
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} ${plural(weeks, ['неделя', 'недели', 'недель'])}`;
  }
  return `${days} ${plural(days, ['день', 'дня', 'дней'])}`;
}

/**
 * «Доступ навсегда после покупки» / «Доступ 8 недель после покупки».
 *
 * Отдельно от `formatProgramAccess` карточки: там срок — свойство продукта,
 * здесь — обещание того, что человек получит, нажав «Купить».
 */
export function accessAfterPurchase(days: number | null, unlimited: boolean): string {
  if (unlimited || days == null) return 'Доступ навсегда после покупки';
  return `Доступ ${formatAccessSpan(days)} после покупки`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Для кого эта программа
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Требования к участнику (шаг 4) — свободный текст мастера. Каждая непустая
 * строка становится пунктом; маркеры, которые мастер мог поставить сам, снимаем,
 * чтобы не рисовать галочку перед «• …».
 */
export function requirementLines(text: string | null): string[] {
  if (!text) return [];
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•·*]\s*/, '').trim())
    .filter((line) => line.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Расписание
// ─────────────────────────────────────────────────────────────────────────────

export interface ScheduleEntry {
  id: number;
  title: string | null;
  startsAtUtc: string;
  durationMinutes: number | null;
}

/**
 * Расписание программы — это её эфиры с датой (§5 ТЗ), собранные по всем
 * разделам и упорядоченные по времени. Эфир без даты в расписание не попадает:
 * он остаётся в составе, но обещать «когда» нечем.
 */
export function liveSchedule(outline: PublicProgramOutline): ScheduleEntry[] {
  const entries: ScheduleEntry[] = [];
  for (const section of outline.sections) {
    for (const it of section.items) {
      if (it.kind !== 'live' || !it.startsAtUtc) continue;
      entries.push({
        id: it.id,
        title: it.title,
        startsAtUtc: it.startsAtUtc,
        durationMinutes: it.durationMinutes,
      });
    }
  }
  return entries.sort((a, b) => Date.parse(a.startsAtUtc) - Date.parse(b.startsAtUtc));
}

// ─────────────────────────────────────────────────────────────────────────────
// CTA
// ─────────────────────────────────────────────────────────────────────────────

export type PageCta =
  /** «Купить программу» → экран покупки (CORE-929). */
  | { kind: 'buy' }
  /** Уже куплено → «Продолжить» в Профиль → Программы (§7 ТЗ). */
  | { kind: 'continue' }
  /** Купить нельзя; текст — готовая причина для человека. */
  | { kind: 'blocked'; message: string };

/**
 * Что стоит на месте главного CTA.
 *
 * Доступ проверяется раньше продаж: купившему программа доступна независимо
 * от того, продаётся ли она сейчас (§2, §4 ТЗ).
 *
 * Отсутствие платёжного контура кнопку не гасит: это временное состояние
 * проекта, а не свойство программы, и рассказать о нём — дело экрана покупки,
 * который знает про контур больше этой страницы.
 *
 * CORE-1536: запрет внешней оплаты (Android вне РФ, iOS) — гасит: это не
 * состояние проекта, а правило магазина, и «Купить» здесь вело бы в тупик.
 */
export function pageCta(page: PublicProgramPage, externalPurchase = true): PageCta {
  if (page.access?.hasAccess) return { kind: 'continue' };
  const block = purchaseBlock(page, true, externalPurchase);
  if (block) return { kind: 'blocked', message: purchaseBlockMessage(block) };
  return { kind: 'buy' };
}
